import { useEffect, useState } from "react";
import "./css/index.css";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "./store";
import WebRoutes from "./routes/web";
import AdminRoutes from "./routes/admin";
import VendorRoutes from "./routes/vendor";
import apiRequest, {
  instance,
  setLogoutFunction,
  setupInterceptors,
} from "./utils/apiRequest";
import { setSiteSetting } from "./store/slice/globalStore";
import Loader from "./components/ui/loader/Loader";
import { Helmet } from "react-helmet";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ScrolltoTop from "./pages/web/ScrolltoTop";
import GlobalDataError from "./components/ui/loader/GlobalDataError";
import CorporateAdminRoutes from "./routes/corporateAdmin";
import TowerHeadAdminRoutes from "./routes/towerHeadAdmin";
import VendorManager from "./routes/vendorManager";

function App() {
  const { handleLogout } = useAuth();
  useEffect(() => {
    setLogoutFunction(handleLogout);
    setupInterceptors(instance);
  }, [handleLogout]);
  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/admin/*" element={<AdminRoutes />} />
        <Route path="/vendor/*" element={<VendorRoutes />} />
        <Route path="/corporate-admin/*" element={<CorporateAdminRoutes />} />
        <Route path="/tower-head-admin/*" element={<TowerHeadAdminRoutes />} />
        <Route path="/vendor-manager/*" element={<VendorManager/>} />
        <Route path="/*" element={<WebRoutes />} />
      </Routes>
    </>
  );
}

function AppInitializer() {
  const setting = useSelector((state) => state.globalState.siteSetting);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  
  const loadSiteSetting = () => {
    apiRequest
      .getSetting()
      .then((data) => {
        dispatch(setSiteSetting(data.data.data.settings));
      })
      .catch((error) => {
        console.error(
          "Failed to load setting?.site_settings?.site_favicon settings:",
          error
        );
        toast.error("Unable to Load data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadSiteSetting();
  }, [dispatch]);


  if (loading) return <Loader />;
  if (
    !loading &&
    (!setting ||
      typeof setting !== "object" ||
      Object.keys(setting).length === 0)
  ) {
    return <GlobalDataError retry={loadSiteSetting} isLoading={loading} />;
  }

  return (
    <BrowserRouter>
      <ScrolltoTop />
      <AuthProvider>
        <Helmet>
          <title>{setting?.seo_settings?.meta_title}</title>
          <meta
            name="description"
            content={setting?.seo_settings?.meta_description}
          />
          <meta
            name="keywords"
            content={setting?.seo_settings?.meta_keywords}
          />
          <meta name="theme-color" content="#4CCD64FF" />

          <meta
            property="og:title"
            content={setting?.seo_settings?.meta_title}
          />
          <meta
            property="og:description"
            content={setting?.seo_settings?.meta_description}
          />

          <meta property="og:url" content={window.location.href} />
          <meta property="og:type" content="website" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content={setting?.seo_settings?.meta_title}
          />
          <meta
            name="twitter:description"
            content={setting?.seo_settings?.meta_description}
          />

          <meta name="author" content={setting?.support_info?.company} />
          <meta
            name="contact:email"
            content={setting?.support_info?.support_email}
          />
          <meta
            name="contact:mobile"
            content={setting?.support_info?.support_mobile}
          />
          <meta
            name="contact:whatsapp"
            content={setting?.support_info?.whatsapp_number}
          />
          <meta
            name="contact:address"
            content={setting?.support_info?.address}
          />
          <meta name="company:cin" content={setting?.support_info?.cin} />
          <meta
            name="company:registered_office"
            content={setting?.support_info?.registered_office}
          />
        </Helmet>
        <App />
      </AuthProvider>
    </BrowserRouter>
  );
}

// Render root
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <AppInitializer />
  </Provider>
);
